<template>
    <div class="flex flex-col gap-4">

        <!-- HEADER -->
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
            Search and edit payee details
        </p>

        <!-- 🔍 SEARCH + RESULTS (HIDE WHEN SELECTED) -->
        <div v-if="!selectedPayee" class="flex flex-col gap-3">

            <!-- SEARCH LABEL -->
            <p class="text-xs font-semibold text-gray-600 dark:text-gray-400">
                Payee Search
            </p>

            <!-- SEARCH -->
            <div class="flex gap-2">
                <input v-model="payeeSearch" type="text" placeholder="Search payee..." class="flex-1 px-3 py-2 text-sm rounded-md border
                        bg-white dark:bg-gray-900
                        border-gray-300 dark:border-gray-700
                        focus:outline-none focus:ring-2 focus:ring-[#E87055]" />

                <button @click="searchPayee" :disabled="isLoadingPayee"
                    class="px-4 py-2 text-sm rounded-md bg-[#E87055] text-white disabled:opacity-50">
                    Search
                </button>
            </div>

            <!-- LOADING -->
            <div v-if="isLoadingPayee" class="text-xs text-gray-500 italic">
                Searching payees...
            </div>

            <!-- RESULTS LABEL -->
            <p v-if="payeeResults.length" class="text-xs font-semibold text-gray-600 dark:text-gray-400">
                Results
            </p>

            <!-- RESULTS -->
            <div v-if="payeeResults.length"
                class="border rounded-md divide-y border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">

                <div v-for="p in payeeResults" :key="p.id" @click="selectPayee(p)"
                    class="p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition">

                    <div class="font-medium text-sm text-gray-800 dark:text-gray-100">
                        {{ p.sold_to }}
                    </div>

                    <div class="text-xs text-gray-500">
                        TIN: {{ p.tin || '—' }}
                    </div>
                </div>
            </div>

            <div v-if="payeeSearch && !payeeResults.length && !isLoadingPayee" class="text-xs text-gray-500 italic">
                No matching payee found.
            </div>

        </div>

        <!-- 🧾 EDIT MODE (ONLY WHEN SELECTED) -->
        <div v-if="selectedPayee"
            class="border rounded-md p-4 space-y-4 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">

            <div class="flex justify-between items-center">
                <p class="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    Payee Information
                </p>

                <!-- BACK BUTTON -->
                <button @click="selectedPayee = null"
                    class="text-xs px-3 py-1 rounded-md bg-gray-500 hover:bg-gray-600 text-white">
                    ← Back
                </button>
            </div>

            <div>
                <label class="text-xs text-gray-500">Sold To</label>
                <input v-model="selectedPayee.sold_to" class="w-full px-3 py-2 text-sm rounded-md border
                        bg-white dark:bg-gray-900
                        border-gray-300 dark:border-gray-700" />
            </div>

            <div>
                <label class="text-xs text-gray-500">TIN</label>
                <input v-model="selectedPayee.tin" class="w-full px-3 py-2 text-sm rounded-md border
                        bg-white dark:bg-gray-900
                        border-gray-300 dark:border-gray-700" />
            </div>

            <div>
                <label class="text-xs text-gray-500">Address</label>
                <textarea v-model="selectedPayee.address" rows="3" class="w-full px-3 py-2 text-sm rounded-md border
                        bg-white dark:bg-gray-900
                        border-gray-300 dark:border-gray-700" />
            </div>

            <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input type="checkbox" v-model="selectedPayee.with_withholding_tax" class="accent-[#E87055]" />
                Withholding Tax
            </label>

            <!-- SAVE -->
            <div class="flex justify-end">
                <button @click="savePayee" :disabled="isSavingPayee"
                    class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-md">
                    {{ isSavingPayee ? 'Saving...' : 'Save Changes' }}
                </button>
            </div>

        </div>

    </div>
</template>

<script>
import { ref, inject } from "vue";

export default {
    setup() {
        const axios = inject("$axios");

        // STATE
        const payeeSearch = ref("");
        const payeeResults = ref([]);
        const selectedPayee = ref(null);

        const isLoadingPayee = ref(false);
        const isSavingPayee = ref(false);

        const successMessage = ref("");
        const errorMessage = ref("");

        const clearMessages = () => {
            successMessage.value = "";
            errorMessage.value = "";
        };

        // SEARCH
        const searchPayee = async () => {
            clearMessages();

            const query = payeeSearch.value?.trim();

            payeeResults.value = [];
            selectedPayee.value = null;

            if (!query) return;

            try {
                isLoadingPayee.value = true;

                const res = await axios.get("/api/payee/get", {
                    params: { query },
                });

                payeeResults.value = res.data?.data || [];
            } catch (err) {
                console.error(err);
                errorMessage.value = "Failed to fetch payees.";
            } finally {
                isLoadingPayee.value = false;
            }
        };

        // SELECT
        const selectPayee = (payee) => {
            clearMessages();

            selectedPayee.value = JSON.parse(JSON.stringify({
                id: payee.id,
                sold_to: payee.sold_to,
                tin: payee.tin,
                address: payee.address,
                with_withholding_tax: payee.with_withholding_tax,
            }));

            payeeResults.value = [];
            payeeSearch.value = payee.sold_to;
        };

        // SAVE
        const savePayee = async () => {
            clearMessages();

            if (!selectedPayee.value?.id || isSavingPayee.value) return;

            try {
                isSavingPayee.value = true;

                await axios.put(`/api/payee/${selectedPayee.value.id}`, {
                    sold_to: selectedPayee.value.sold_to,
                    tin: selectedPayee.value.tin,
                    address: selectedPayee.value.address,
                    with_withholding_tax: selectedPayee.value.with_withholding_tax,
                });

                successMessage.value = "Payee updated successfully.";

                setTimeout(() => {
                    successMessage.value = "";
                }, 2500);

            } catch (err) {
                console.error(err);
                errorMessage.value = "Failed to update payee.";
            } finally {
                isSavingPayee.value = false;
            }
        };

        return {
            payeeSearch,
            payeeResults,
            selectedPayee,
            isLoadingPayee,
            isSavingPayee,
            successMessage,
            errorMessage,
            searchPayee,
            selectPayee,
            savePayee
        };
    }
};
</script>